import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Star, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function EverythingYourPetNeedsHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="13393" id="ocean-to-village-hero" className="relative w-full overflow-hidden bg-background text-foreground py-16 lg:py-28 xl:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/abstract_leaf_pattern.png)] bg-cover bg-center opacity-10 mix-blend-overlay" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-background/60" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 md:px-6 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8 items-center">
          {}
          <div className="lg:col-span-7 flex flex-col gap-8 text-center lg:text-left">
            <div className="space-y-4">
              <Badge variant="outline" className="w-fit mx-auto lg:mx-0 border-secondary/30 text-secondary bg-secondary/10 px-4 py-1.5 rounded-full text-sm font-medium tracking-wide uppercase">
                Exclusive Delivery
              </Badge>
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground leading-[1.1]">
                From the Deep Blue <br className="hidden lg:block" />
                <span className="text-secondary">to Your Village Doorstep</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 font-sans leading-relaxed">
                Village Seafood brings the ocean's finest catch directly to your kitchen. We offer premium frozen seafood delivery exclusively for residents of The Villages, ensuring restaurant-quality freshness and convenience from the coast to your door.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button asChild size="lg" className="bg-chart-3 hover:opacity-90 hover:bg-chart-3 text-white border-none shadow-xl shadow-chart-3/20 rounded-full px-8 h-14 text-lg transition-transform hover:-translate-y-0.5">
                <Link to="/ProductsPage">
                  Browse Today's Catch <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="text-foreground hover:bg-secondary/10 rounded-full px-8 h-14 text-lg border border-transparent hover:border-secondary/20">
                <Link to="/FreshFrozenStandardPage">The Standard</Link>
              </Button>
            </div>

            {}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6 pt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="w-10 h-10 rounded-full border-2 border-background bg-muted overflow-hidden">
                      <img src={`/img/avatar_user_${i}.png`} alt={`Happy Resident ${i}`} className="w-full h-full object-cover" />
                    </div>)}
                </div>
                <div className="flex flex-col items-start ml-2">
                  <div className="flex text-chart-3">
                    {[1, 2, 3, 4, 5].map(star => <Star key={star} data-index={star} className="w-3.5 h-3.5 fill-current" />)}
                  </div>
                  <span className="font-medium text-foreground">
                    5k+ Happy Neighbors
                  </span>
                </div>
              </div>
              <div className="hidden sm:block w-px h-8 bg-border" />
              <div className="flex items-center gap-2">
                <CircleCheck className="w-5 h-5 text-secondary" />
                <span>Cold-Chain Certified</span>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 relative h-[450px] md:h-[550px] lg:h-[650px] w-full hidden md:block">
            <div className="relative w-full h-full perspective-1000">
              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-secondary/10 rounded-full blur-3xl -z-10" />

              {}
              <div className="absolute top-0 right-0 w-[85%] h-[60%] rounded-[2.5rem] rounded-tr-[5rem] overflow-hidden shadow-2xl border-4 border-white/20 z-10 transition-transform hover:scale-[1.02] duration-500">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/golden_retriever_hero.png" alt="Fresh Florida Grouper on Ice" className="w-full h-full object-cover" />
              </div>

              {}
              <div className="absolute bottom-4 left-4 w-[65%] h-[50%] rounded-[2.5rem] rounded-bl-[5rem] overflow-hidden shadow-2xl border-4 border-white/20 z-20 transition-transform hover:scale-[1.02] duration-500 delay-100">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/maine_coon_hero.png" alt="Luxury Waterfront Village in Florida" className="w-full h-full object-cover" />
              </div>

              {}
              <div className="absolute top-[45%] left-0 z-30 animate-bounce duration-[4000ms]">
                <Badge variant="secondary" className="px-5 py-2.5 text-sm shadow-xl bg-white/40 backdrop-blur-md text-foreground border border-white/30 rounded-full flex gap-2.5 items-center">
                  <span className="bg-chart-3/20 text-chart-3 p-1.5 rounded-full">
                    <Star className="w-3.5 h-3.5 fill-current" />
                  </span>
                  <div className="flex flex-col leading-none">
                    <span className="font-bold text-xs uppercase tracking-wider text-muted-foreground">
                      Quality
                    </span>
                    <span className="font-bold text-sm">#1 Local Choice</span>
                  </div>
                </Badge>
              </div>
            </div>
          </div>

          {}
          <div className="md:hidden col-span-12 mt-8">
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-xl border-4 border-white/20 aspect-[4/3]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/golden_retriever_hero.png" alt="Fresh Seafood Delivery" className="w-full h-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4">
                <Badge className="w-full justify-center bg-white/60 text-foreground backdrop-blur-md shadow-sm py-2 border-white/30">
                  Ocean Fresh to Your Door
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}